// Client Claude avec fallback automatique
import { getClaudeClient, callClaude } from './claude-client'
import { getActiveMedications } from './medications'
import { supabase } from './supabase'
import type {
  AIAnalysis,
  AIAnalysisInput,
  AnalysisType,
  HealthEntry,
  Medication,
  LabResult
} from '@/types/health'

// ================================
// FONCTIONS D'ANALYSE IA
// ================================

export async function analyzeHealthData(
  patientId: string,
  analysisType: AnalysisType,
  additionalContext?: Record<string, any>
): Promise<AIAnalysis> {
  try {
    // Récupérer les données du patient
    const patientData = await gatherPatientData(patientId)

    // Préparer le prompt selon le type d'analyse
    const prompt = createAnalysisPrompt(analysisType, patientData, additionalContext)

    // Appeler Claude via le client robuste (AWS Bedrock + fallback API Anthropic)
    const claudeClient = getClaudeClient()

    const claudeResponse = await claudeClient.generateResponse(
      [{ role: 'user', content: prompt }],
      getSystemPrompt(analysisType),
      2000,
      0.3
    )

    // Traiter la réponse
    const aiResponse = parseClaudeResponse({ text: claudeResponse })

    // Sauvegarder l'analyse
    const analysisData: AIAnalysisInput = {
      patient_id: patientId,
      analysis_type: analysisType,
      input_data: {
        patient_data: patientData,
        additional_context: additionalContext || {}
      },
      ai_response: aiResponse,
      confidence_score: aiResponse.confidence_score || null,
      specialist_recommendations: aiResponse.specialist_recommendations || [],
      suggested_tests: aiResponse.suggested_tests || [],
      risk_factors: aiResponse.risk_factors || [],
      follow_up_needed: aiResponse.follow_up_needed || false,
      follow_up_urgency: aiResponse.follow_up_urgency || null
    }

    return await saveAIAnalysis(analysisData)
  } catch (error) {
    throw new Error(`Erreur lors de l'analyse IA: ${error instanceof Error ? error.message : 'Erreur inconnue'}`)
  }
}

export async function generateSpecialistRecommendation(
  patientId: string,
  symptoms: string[],
  recentEntries: HealthEntry[]
): Promise<AIAnalysis> {
  return analyzeHealthData(patientId, 'specialist_recommendation', {
    symptoms,
    recent_entries: recentEntries
  })
}

export async function suggestMedicalTests(
  patientId: string,
  concerningSymptoms: string[],
  lastTestDate?: string
): Promise<AIAnalysis> {
  return analyzeHealthData(patientId, 'test_suggestion', {
    concerning_symptoms: concerningSymptoms,
    last_test_date: lastTestDate
  })
}

export async function analyzeMedicationEffectiveness(
  patientId: string,
  medications: Medication[],
  healthEntries: HealthEntry[]
): Promise<AIAnalysis> {
  return analyzeHealthData(patientId, 'medication_review', {
    medications,
    health_entries: healthEntries
  })
}

// Fonction générique pour les analyses simples
export async function generateAnalysis(
  patientId: string,
  analysisType: AnalysisType | 'voice_analysis' | 'comprehensive_health',
  customPrompt: string | { healthEntries?: any[], medications?: any[] }
): Promise<{ content: string, summary?: string, recommendations?: string, key_findings?: string }> {
  try {
    const claudeClient = getClaudeClient()

    // Gestion du prompt personnalisé ou objet de données
    let finalPrompt = ''
    if (typeof customPrompt === 'string') {
      finalPrompt = customPrompt
    } else {
      // Construire le prompt à partir des données
      finalPrompt = `Analysez ces données médicales et fournissez une synthèse complète :

Données de santé récentes : ${JSON.stringify(customPrompt.healthEntries?.slice(0, 10) || [], null, 2)}

Traitements actuels : ${JSON.stringify(customPrompt.medications || [], null, 2)}

Veuillez fournir :
1. Un résumé global de l'état de santé
2. Les tendances importantes observées
3. Les points d'attention
4. Les recommandations médicales`
    }

    const systemPrompt = `Vous êtes un assistant médical IA spécialisé dans l'analyse de données de santé pour une patiente souffrant de RCH (Rectocolite Hémorragique) et d'endométriose suspectée.

Fournissez des analyses claires, concises et basées sur des preuves médicales. Restez dans votre domaine de compétence et recommandez toujours de consulter un professionnel de santé pour les décisions médicales importantes.`

    const response = await claudeClient.generateResponse(
      [{ role: 'user', content: finalPrompt }],
      systemPrompt,
      1000,
      0.3
    )

    // Essayer de parser une réponse structurée
    try {
      const structuredResponse = JSON.parse(response)
      return {
        content: response,
        summary: structuredResponse.summary || structuredResponse.résumé || '',
        recommendations: structuredResponse.recommendations || structuredResponse.recommandations || '',
        key_findings: structuredResponse.key_findings || structuredResponse.points_clés || ''
      }
    } catch {
      // Si ce n'est pas du JSON, retourner tel quel
      return {
        content: response,
        summary: response.split('\n')[0] || '', // Premier ligne comme résumé
        recommendations: '',
        key_findings: ''
      }
    }

  } catch (error: any) {
    console.error('❌ Erreur lors de l\'analyse IA:', error)
    return {
      content: 'Analyse temporairement indisponible. Les services IA sont en cours de configuration.',
      summary: 'Service temporairement indisponible',
      recommendations: 'Veuillez réessayer dans quelques minutes.',
      key_findings: ''
    }
  }
}

// ================================
// FONCTIONS CRUD POUR LES ANALYSES
// ================================

export async function saveAIAnalysis(analysis: AIAnalysisInput): Promise<AIAnalysis> {
  const { data, error } = await supabase
    .from('ai_analyses')
    .insert(analysis)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la sauvegarde de l'analyse: ${error.message}`)
  }

  return data
}

export async function getAIAnalyses(
  patientId: string,
  analysisType?: AnalysisType,
  limit = 10
): Promise<AIAnalysis[]> {
  let query = supabase
    .from('ai_analyses')
    .select('*')
    .eq('patient_id', patientId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (analysisType) {
    query = query.eq('analysis_type', analysisType)
  }

  const { data, error } = await query

  if (error) {
    throw new Error(`Erreur lors de la récupération des analyses: ${error.message}`)
  }

  return data || []
}

export async function markAnalysisReviewed(
  analysisId: string,
  doctorId: string,
  doctorNotes?: string
): Promise<AIAnalysis> {
  const { data, error } = await supabase
    .from('ai_analyses')
    .update({
      is_reviewed_by_doctor: true,
      reviewed_by: doctorId,
      reviewed_at: new Date().toISOString(),
      doctor_notes: doctorNotes || null
    })
    .eq('id', analysisId)
    .select()
    .single()

  if (error) {
    throw new Error(`Erreur lors de la mise à jour de l'analyse: ${error.message}`)
  }

  return data
}

// ================================
// FONCTIONS UTILITAIRES
// ================================

async function gatherPatientData(patientId: string) {
  try {
    // Récupérer les données de santé récentes
    const { data: healthEntries } = await supabase
      .from('health_entries')
      .select('*')
      .eq('owner_id', patientId)
      .order('entry_date', { ascending: false })
      .limit(30)

    // Récupérer les médicaments actifs
    const medications = await getActiveMedications(patientId)

    // Récupérer les résultats d'analyses récentes
    const { data: labResults } = await supabase
      .from('lab_results')
      .select('*')
      .eq('patient_id', patientId)
      .order('test_date', { ascending: false })
      .limit(20)

    return {
      health_entries: healthEntries || [],
      medications: medications || [],
      lab_results: labResults || []
    }
  } catch (error) {
    console.error('Erreur lors de la collecte des données patient:', error)
    return { health_entries: [], medications: [], lab_results: [] }
  }
}

function getSystemPrompt(analysisType: AnalysisType): string {
  const basePrompt = `Vous êtes un assistant médical IA spécialisé dans l'analyse de données de santé pour une patiente souffrant de RCH (Rectocolite Hémorragique) et d'endométriose suspectée.

IMPORTANT:
- Vous n'êtes PAS un médecin et ne posez PAS de diagnostic
- Vos suggestions sont des ORIENTATIONS pour aider les médecins
- Recommandez TOUJOURS de consulter un professionnel de santé
- Soyez prudent avec les recommandations d'urgence

Format de réponse attendu en JSON:
{
  "analysis_summary": "Résumé de l'analyse en français",
  "key_findings": ["Point 1", "Point 2", "Point 3"],
  "specialist_recommendations": ["Spécialité recommandée 1", "Spécialité 2"],
  "suggested_tests": ["Examen suggéré 1", "Examen 2"],
  "risk_factors": ["Facteur de risque identifié 1", "Facteur 2"],
  "follow_up_needed": true/false,
  "follow_up_urgency": "low/medium/high/urgent",
  "confidence_score": 0.85,
  "recommendations": "Recommandations détaillées en français"
}`

  switch (analysisType) {
    case 'health_trends':
      return basePrompt + `\n\nVous analysez les TENDANCES de santé sur les dernières semaines. Identifiez les patterns, améliorations ou détériorations.`

    case 'symptom_analysis':
      return basePrompt + `\n\nVous analysez les SYMPTÔMES rapportés pour identifier des corrélations et des patterns. Focus sur RCH et endométriose.`

    case 'medication_review':
      return basePrompt + `\n\nVous évaluez l'EFFICACITÉ des traitements actuels et identifiez les effets secondaires potentiels.`

    case 'specialist_recommendation':
      return basePrompt + `\n\nVous recommandez les SPÉCIALISTES appropriés selon les symptômes et l'évolution de l'état de santé.`

    case 'test_suggestion':
      return basePrompt + `\n\nVous suggérez les EXAMENS COMPLÉMENTAIRES pertinents selon les symptômes et l'historique médical.`

    default:
      return basePrompt
  }
}

function createAnalysisPrompt(
  analysisType: AnalysisType,
  patientData: any,
  additionalContext?: Record<string, any>
): string {
  let prompt = `Analysez les données médicales suivantes pour une patiente avec RCH et endométriose suspectée:\n\n`

  // Ajouter les données de santé
  if (patientData.health_entries?.length > 0) {
    prompt += `ENTRÉES SANTÉ RÉCENTES (${patientData.health_entries.length} dernières):\n`
    patientData.health_entries.slice(0, 10).forEach((entry: HealthEntry) => {
      prompt += `- ${entry.entry_date}: Douleur ${entry.pain_level || 'N/A'}/10, Énergie ${entry.energy_level || 'N/A'}/10, Sommeil ${entry.sleep_quality || 'N/A'}/10`
      if (entry.symptoms?.length > 0) prompt += `, Symptômes: ${entry.symptoms.join(', ')}`
      if (entry.summary) prompt += `, Résumé: ${entry.summary}`
      prompt += `\n`
    })
    prompt += `\n`
  }

  // Ajouter les médicaments
  if (patientData.medications?.length > 0) {
    prompt += `MÉDICAMENTS ACTUELS:\n`
    patientData.medications.forEach((med: Medication) => {
      prompt += `- ${med.name} (${med.dosage || 'dosage non précisé'}) - ${med.frequency || 'fréquence non précisée'}`
      if (med.effectiveness_rating) prompt += ` - Efficacité: ${med.effectiveness_rating}/10`
      prompt += `\n`
    })
    prompt += `\n`
  }

  // Ajouter les résultats de laboratoire
  if (patientData.lab_results?.length > 0) {
    prompt += `RÉSULTATS D'ANALYSES RÉCENTES:\n`
    patientData.lab_results.slice(0, 10).forEach((result: LabResult) => {
      prompt += `- ${result.test_name}: ${result.result_value || result.result_text || 'N/A'} ${result.unit || ''}`
      if (result.is_abnormal) prompt += ` (ANORMAL)`
      prompt += ` (${result.test_date})\n`
    })
    prompt += `\n`
  }

  // Ajouter le contexte spécifique
  if (additionalContext) {
    prompt += `CONTEXTE SUPPLÉMENTAIRE:\n${JSON.stringify(additionalContext, null, 2)}\n\n`
  }

  // Ajouter la demande spécifique selon le type
  switch (analysisType) {
    case 'health_trends':
      prompt += `Analysez les TENDANCES de santé. Identifiez les améliorations, détériorations, et patterns récurrents.`
      break
    case 'symptom_analysis':
      prompt += `Analysez les SYMPTÔMES pour identifier des corrélations et des patterns en relation avec RCH et endométriose.`
      break
    case 'medication_review':
      prompt += `Évaluez l'EFFICACITÉ des traitements actuels et identifiez les ajustements potentiels.`
      break
    case 'specialist_recommendation':
      prompt += `Recommandez les SPÉCIALISTES appropriés selon l'état actuel et l'évolution des symptômes.`
      break
    case 'test_suggestion':
      prompt += `Suggérez les EXAMENS COMPLÉMENTAIRES pertinents pour approfondir le diagnostic ou le suivi.`
      break
  }

  prompt += `\n\nRépondez uniquement en JSON valide selon le format spécifié.`

  return prompt
}

function parseClaudeResponse(content: any): any {
  try {
    if (content.type === 'text') {
      const text = content.text
      // Extraire le JSON de la réponse
      const jsonMatch = text.match(/\{[\s\S]*\}/)
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0])
      }
    }
    throw new Error('Format de réponse invalide')
  } catch (error) {
    console.error('Erreur lors du parsing de la réponse Claude:', error)
    return {
      analysis_summary: 'Erreur lors de l\'analyse',
      key_findings: [],
      specialist_recommendations: [],
      suggested_tests: [],
      risk_factors: [],
      follow_up_needed: false,
      follow_up_urgency: 'low',
      confidence_score: 0,
      recommendations: 'Une erreur s\'est produite lors de l\'analyse. Veuillez réessayer.'
    }
  }
}

// ================================
// FONCTIONS D'ASSISTANCE
// ================================

export function getAnalysisTypeLabel(type: AnalysisType): string {
  switch (type) {
    case 'health_trends':
      return 'Analyse des tendances'
    case 'symptom_analysis':
      return 'Analyse des symptômes'
    case 'medication_review':
      return 'Évaluation des traitements'
    case 'specialist_recommendation':
      return 'Orientation spécialiste'
    case 'test_suggestion':
      return 'Suggestion d\'examens'
    default:
      return type
  }
}

export function getUrgencyColor(urgency: string): string {
  switch (urgency) {
    case 'urgent':
      return 'text-red-400 bg-red-400/10 border-red-400/20'
    case 'high':
      return 'text-orange-400 bg-orange-400/10 border-orange-400/20'
    case 'medium':
      return 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20'
    case 'low':
      return 'text-green-400 bg-green-400/10 border-green-400/20'
    default:
      return 'text-gray-400 bg-gray-400/10 border-gray-400/20'
  }
}

export function getUrgencyLabel(urgency: string): string {
  switch (urgency) {
    case 'urgent':
      return 'Urgent'
    case 'high':
      return 'Élevé'
    case 'medium':
      return 'Modéré'
    case 'low':
      return 'Faible'
    default:
      return urgency
  }
}
